/**
 * DynamicValueInput Component
 *
 * Routes to the appropriate value input component based on field type and operator.
 * Handles all input variations: string, number, enum, multi-select, paste list, etc.
 */

import { useQuery } from '@tanstack/react-query'
import { metadataService } from '@/services/metadataService'
import {
  operatorRequiresValue,
  operatorRequiresMultipleValues,
  operatorRequiresRange,
} from '../../operatorConfig'
import { StringInput } from './StringInput'
import { NumberInput } from './NumberInput'
import { NoValueInput } from './NoValueInput'
import { EnumSingleSelect } from './EnumSingleSelect'
import { EnumMultiSelect } from './EnumMultiSelect'
import { DateInput } from './DateInput'
import { DateRangeInput } from './DateRangeInput'
import { NumberRangeInput } from './NumberRangeInput'
import type { DynamicValueInputProps } from '../../advancedFiltersTypes'

export function DynamicValueInput({
  field,
  model,
  fields: passedFields,
  operator,
  value,
  values,
  value2,
  onChange,
  endpoint,
  baseFilters,
  hasLifecycle,
}: DynamicValueInputProps) {
  // Only fetch field metadata if not passed from parent
  const { data: metadataResponse } = useQuery({
    queryKey: ['metadata', model, 'fields'],
    queryFn: async () => {
      const response = await metadataService.getNonSystemFields(model)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch metadata')
      }
      return response.data
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    // Skip fetch if fields are passed from parent
    enabled: !passedFields,
  })

  // Use passed fields or fetched fields
  const fieldMetadata = passedFields?.[field] ?? metadataResponse?.fields?.[field]

  // Construct API endpoint from model name if not provided
  // Model names are singular (e.g., 'offering'), endpoints are plural (e.g., '/offerings')
  // Uses metadataService.modelToEndpoint for proper pluralization (handles 'currency' -> '/currencies', etc.)
  const effectiveEndpoint = endpoint ?? (model ? metadataService.modelToEndpoint(model) : undefined)

  // If no field selected or metadata not loaded, show placeholder
  if (!field || !fieldMetadata) {
    return <NoValueInput />
  }

  // Check if operator requires value
  if (
    !operatorRequiresValue(operator) &&
    !operatorRequiresMultipleValues(operator) &&
    !operatorRequiresRange(operator)
  ) {
    return <NoValueInput />
  }

  const fieldType = fieldMetadata.py_type || 'string'
  const hasAllowedValues = fieldMetadata.allowed_values && fieldMetadata.allowed_values.length > 0

  // Determine if we should use dynamic distinct values (autocomplete feature)
  // Only use dynamic fetch when:
  // 1. No static allowed_values defined (those always take precedence)
  // 2. autocomplete flag is set in metadata
  // 3. endpoint is available (provided or constructed from model)
  const useAutocomplete = !hasAllowedValues && fieldMetadata.autocomplete === true && !!effectiveEndpoint

  // Handle range operators (BETWEEN) - check before multi-value operators
  if (operatorRequiresRange(operator)) {
    // Date/timestamp range
    if (fieldType === 'date' || fieldType === 'timestamp') {
      return (
        <DateRangeInput
          value={value}
          value2={value2}
          onChange={(newValue, newValue2) =>
            onChange({ value: newValue, value2: newValue2 })
          }
        />
      )
    }

    // Numeric range
    if (fieldType === 'numeric' || fieldType === 'integer' || fieldType === 'float') {
      return (
        <NumberRangeInput
          value={value}
          value2={value2}
          onChange={(newValue, newValue2) =>
            onChange({ value: newValue, value2: newValue2 })
          }
        />
      )
    }
  }

  // Handle multi-value operators (IN, NOT_IN)
  if (operatorRequiresMultipleValues(operator)) {
    // Determine field type for validation
    let validationFieldType: 'string' | 'uuid' | 'numeric' = 'string'
    if (fieldType === 'uuid') {
      validationFieldType = 'uuid'
    } else if (fieldType === 'numeric' || fieldType === 'integer' || fieldType === 'float') {
      validationFieldType = 'numeric'
    }

    return (
      <EnumMultiSelect
        value={values}
        onChange={(newValues) => onChange({ values: newValues })}
        fieldMetadata={fieldMetadata}
        fieldType={validationFieldType}
        fieldName={field}
        // Dynamic distinct values support
        endpoint={effectiveEndpoint}
        useDynamicValues={useAutocomplete}
        baseFilters={baseFilters}
        hasLifecycle={hasLifecycle}
      />
    )
  }

  // Handle single-value operators based on field type
  // Enum fields with allowed_values OR autocomplete enabled
  if (hasAllowedValues || useAutocomplete) {
    return (
      <EnumSingleSelect
        value={value}
        onChange={(newValue) => onChange({ value: newValue })}
        fieldMetadata={fieldMetadata}
        // Dynamic distinct values support
        endpoint={effectiveEndpoint}
        fieldName={field}
        useDynamicValues={useAutocomplete}
        baseFilters={baseFilters}
        hasLifecycle={hasLifecycle}
      />
    )
  }

  // Date/timestamp fields
  if (fieldType === 'date' || fieldType === 'timestamp') {
    return (
      <DateInput
        value={value}
        onChange={(newValue) => onChange({ value: newValue })}
      />
    )
  }

  // Numeric fields
  if (fieldType === 'numeric' || fieldType === 'integer' || fieldType === 'float') {
    return (
      <NumberInput
        value={value}
        onChange={(newValue) => onChange({ value: newValue })}
      />
    )
  }

  // Boolean fields
  if (fieldType === 'boolean') {
    return (
      <EnumSingleSelect
        value={value}
        onChange={(newValue) => onChange({ value: newValue })}
        fieldMetadata={{
          ...fieldMetadata,
          allowed_values: [
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ],
        }}
      />
    )
  }

  // Default: String input for string, uuid, etc.
  return (
    <StringInput
      value={value}
      onChange={(newValue) => onChange({ value: newValue })}
      placeholder={`Enter ${fieldMetadata.title || field}`}
    />
  )
}
